import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Activity, AlertTriangle, ArrowRight, ArrowUpRight, Camera, CheckCircle, ChevronDown, ChevronRight, Info } from 'react-feather';
import {
  currentActor,
  fetchTasksMe,
  HealthBand,
  isTasksLeaderRole,
  patchCachedTasksMe,
  PortfolioHealth,
  RM_TASKS_ENABLED,
  Task,
  TasksMe,
  tasksApi,
  AgentHealthTrends,
  HealthTrends,
  TaskTrends,
  TaskTypeCfg,
  TrendGranularity,
  AgentTypeBreakdown,
  BREAKDOWN_RANGES,
  BreakdownRangeKey,
  fetchAgentTypeBreakdown,
  istDay,
  rangeDates,
  shiftRange,
} from '../../../Tasks/tasksApi';
import {
  Button,
  Card,
  DescriptionList,
  EmptyState,
  Loader,
  Page,
  PageHeader,
  Select,
  Table,
  TextInput,
} from '../../../ui';
import type { BadgeTone } from '../../../ui';
import { Avatar, Callout, HEALTH_TONE, Segmented, StatTile, Swatch } from './ui-pending';
import type { StatTone } from './ui-pending';
import { CountryFlag, TaskIdChip } from './TaskActionModal';
import { uploadMyPhoto } from '../../../Tasks/agentPhoto';
import StudentDrawer, { StudentRef } from './StudentDrawer';
import './RmDashboardPage.scss';

/**
 * Consumer Xperience → RM Dashboard: the RM's morning briefing.
 *
 * "Top priority actions" is LIVE — every non-completed task, ranked.
 * Stat cards and Portfolio health are still DUMMY (constant below) until
 * Kartheek specifies the stats APIs; wiring them = replace DUMMY and drop
 * the "Sample data" badges.
 */

/** Rank-chip tones by task priority, echoing the mockup's severity colors. */
const RANK_TONE: Record<Task['priority'], BadgeTone> = {
  URGENT: 'danger',
  HIGH: 'warning',
  MEDIUM: 'primary',
  LOW: 'success',
};

const PRIORITY_WEIGHT: Record<Task['priority'], number> = {
  URGENT: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};

const BAND_DISPLAY: { band: HealthBand; label: string }[] = [
  { band: 'HEALTHY', label: 'Healthy' },
  { band: 'WATCHLIST', label: 'Watchlist' },
  { band: 'AT_RISK', label: 'At Risk' },
  { band: 'CRITICAL', label: 'Critical' },
];

const monthLabel = (yyyyMm: string): string =>
  new Date(`${yyyyMm}-01T00:00:00Z`).toLocaleString('en-IN', { month: 'short', timeZone: 'UTC' });


/**
 * The date controls shared by Team overview and the agent/type breakdown:
 * presets, two pickers, and arrows that step by the window's own length.
 * One implementation so the two sections can't drift apart — ops read them
 * side by side.
 */
/**
 * What each column means, in ops' words. Two of these caused a real
 * misreading — a tile said 26 "overdue" for an RM who had worked all 26 —
 * so the definitions live on screen rather than in someone's memory.
 */
const METRIC_HELP: { term: string; text: string }[] = [
  { term: 'Students', text: 'Learners currently mapped to this RM. Always today, whatever date range is chosen.' },
  { term: 'Open', text: 'Tasks not yet closed.' },
  { term: 'Not started', text: 'Open, and nobody has logged anything on it yet.' },
  { term: 'In progress', text: 'The RM has logged a call, note or activity, but it is not closed.' },
  { term: 'Due today', text: 'Action deadline falls today and has not passed.' },
  { term: 'Overdue', text: 'The action SLA was missed: nobody has touched it and its deadline has passed. A task being worked is never overdue, however long it stays open.' },
  { term: 'Past resolution', text: 'Being worked, but past the day the sheet says it should have been resolved (24hrs, 3 days, 7 days by type).' },
  { term: 'Renewals / Onboarding', text: 'Open tasks of that type, for a quick read of what the RM is carrying.' },
  { term: 'Closed', text: 'Closed within the chosen range — by when it was closed, not when it was raised. With no range: the last 7 days.' },
  { term: 'Avg score', text: "Average health score across this RM's learners (0–100)." },
  { term: 'Health', text: 'Learners by band: healthy 75+, watchlist 60–74, at risk 40–59, critical below 40.' },
];


/** A quiet "what do these mean?" that opens a definition list. */
const MetricLegend: React.FC<{ only?: string[] }> = ({ only }) => {
  const [show, setShow] = useState(false);
  const items = only ? METRIC_HELP.filter((m) => only.includes(m.term)) : METRIC_HELP;
  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        icon={<Info size={14} aria-hidden />}
        onClick={() => setShow((v) => !v)}
        aria-expanded={show}
      >
        {show ? 'Hide definitions' : 'What do these mean?'}
      </Button>
      {show && (
        <div className="rmd-legend">
          <DescriptionList items={items.map((m) => ({ label: m.term, value: m.text }))} />
        </div>
      )}
    </>
  );
};

/**
 * Native date inputs only open their calendar from the icon; clicking the
 * text just focuses the field, which reads as broken. showPicker() opens it
 * from anywhere in the box. Unsupported or blocked (it needs a real user
 * gesture) → the icon still works, so there's nothing to fall back to.
 */
const openCalendar = (e: React.MouseEvent<HTMLInputElement>): void => {
  const el = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
  try {
    el.showPicker?.();
  } catch {
    /* browser declined — the calendar icon still works */
  }
};

const useDateRange = (): {
  from: string;
  to: string;
  dated: boolean;
  caption: string;
  control: JSX.Element;
} => {
  // This month, not all time. All time made the breakdown rescan every task
  // ever raised on each load, and a leader opening the dashboard is asking
  // about now — the other presets are a click away.
  const [range, setRange] = useState<BreakdownRangeKey>('month');
  const [from, setFrom] = useState(() => rangeDates('month').from);
  const [to, setTo] = useState(() => rangeDates('month').to);

  const choosePreset = (key: BreakdownRangeKey): void => {
    setRange(key);
    if (key === 'custom') return;
    const d = rangeDates(key);
    setFrom(d.from);
    setTo(d.to);
  };
  const step = (direction: -1 | 1): void => {
    const n = shiftRange(from, to, direction);
    setRange('custom');
    setFrom(n.from);
    setTo(n.to);
  };
  const dated = !!(from && to);
  const niceDay = (d: string): string =>
    new Date(`${d}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });

  const control = (
    <div className="rmd-range">
      <Select
        size="sm"
        aria-label="Date range"
        className="rmd-range__select"
        value={range}
        options={BREAKDOWN_RANGES.map((r) => ({ value: r.key, label: r.label }))}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          choosePreset(e.target.value as BreakdownRangeKey)
        }
      />
      <TextInput
        type="date"
        size="sm"
        aria-label="From date"
        className="rmd-range__date"
        onClick={openCalendar}
        value={from}
        max={to || undefined}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const d = e.target.value;
          setRange('custom');
          setFrom(d);
          if (d && (!to || to < d)) setTo(d);
        }}
      />
      <span className="rm-caption">to</span>
      <TextInput
        type="date"
        size="sm"
        aria-label="To date"
        className="rmd-range__date"
        onClick={openCalendar}
        value={to}
        min={from || undefined}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setRange('custom');
          setTo(e.target.value);
        }}
      />
      {dated && (
        <>
          <Button size="sm" variant="ghost" title="Previous period" aria-label="Previous period" onClick={() => step(-1)}>
            ‹
          </Button>
          <Button size="sm" variant="ghost" title="Next period" aria-label="Next period" disabled={to >= istDay(0)} onClick={() => step(1)}>
            ›
          </Button>
          <Button size="sm" variant="ghost" title="Back to all time" onClick={() => choosePreset('all')}>
            Clear
          </Button>
        </>
      )}
    </div>
  );

  const caption = !dated
    ? ''
    : from === to
      ? `${niceDay(from)} (IST)`
      : `${niceDay(from)} – ${niceDay(to)} (IST)`;

  return { from, to, dated, caption, control };
};


/**
 * Companion to Team overview: open tasks pivoted agent × task-type — one row
 * per agent, one column per type, with per-agent totals (row) and per-type
 * totals (column). Computed from the loaded open tasks (whole team for a
 * leader). Overdue-per-agent lives on the Team Performance screen.
 */
const UNASSIGNED_KEY = '__unassigned__';
interface TypeStat { key: string; name: string; open: number; overdue: number; closed: number }

/** Per-type open/overdue/closed counts from an agent's open + closed task lists. */
const buildTypeStats = (open: Task[], closed: Task[]): TypeStat[] => {
  const m = new Map<string, TypeStat>();
  const row = (t: Task): TypeStat => {
    let r = m.get(t.taskType.key);
    if (!r) { r = { key: t.taskType.key, name: t.taskType.name, open: 0, overdue: 0, closed: 0 }; m.set(t.taskType.key, r); }
    return r;
  };
  for (const t of open) { const r = row(t); r.open += 1; if (isOverdue(t)) r.overdue += 1; }
  for (const t of closed) row(t).closed += 1;
  return Array.from(m.values()).sort((a, b) => b.open + b.closed - (a.open + a.closed));
};

const TaskTypeBreakdown: React.FC = () => {
  // Same controls as Team overview above, from the shared hook — ops read
  // the two side by side, so they must behave identically.
  const { from, to, dated, caption, control } = useDateRange();
  const [data, setData] = useState<AgentTypeBreakdown | null>(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setError('');
    fetchAgentTypeBreakdown({ from, to })
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e.message || 'Could not load the breakdown'));
    return () => {
      live = false;
    };
  }, [from, to]);

  // Columns are the types present, busiest first; rows come sorted already.
  const types = useMemo(() => {
    if (!data) return [] as { key: string; name: string; total: number }[];
    const totals = new Map<string, { key: string; name: string; total: number }>();
    for (const a of data.agents) {
      for (const t of a.types) {
        const cur = totals.get(t.taskTypeKey) ?? { key: t.taskTypeKey, name: t.taskTypeName, total: 0 };
        cur.total += t.open + t.inProgress;
        totals.set(t.taskTypeKey, cur);
      }
    }
    return Array.from(totals.values()).sort((a, b) => b.total - a.total);
  }, [data]);

  const grand = data?.agents.reduce((n, a) => n + a.openTotal + a.inProgressTotal, 0) ?? 0;

  return (
    <Card
      title="Open tasks by agent & type"
      subtitle={
        dated
          ? `Raised ${caption} · closed in the same window`
          : 'Everything still open, and everything ever closed'
      }
      actions={
        <div className="rmd-card-actions">
          {control}
          <span className="rm-caption rmd-nowrap">{grand} open</span>
          <MetricLegend
            only={['Not started', 'In progress', 'Overdue', 'Past resolution', 'Closed']}
          />
        </div>
      }
    >
      {error ? (
        <Callout tone="danger">{error}</Callout>
      ) : !data ? (
        <Loader />
      ) : data.agents.length === 0 ? (
        <EmptyState title="Nothing open in this period." description="Try a wider date range." />
      ) : (
        <Table caption="Open tasks by agent and type">
          <thead>
            <tr>
              <th>Agent</th>
              {types.map((t) => (
                <th key={t.key} className="ds-num">{t.name}</th>
              ))}
              <th className="ds-num">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.agents.map((a) => {
              const key = a.agentExternalId ?? UNASSIGNED_KEY;
              const isUn = !a.agentExternalId;
              const isOpen = expanded === key;
              const byType = new Map(a.types.map((t) => [t.taskTypeKey, t]));
              return (
                <React.Fragment key={key}>
                  <tr
                    role="button"
                    aria-expanded={isOpen}
                    onClick={() => setExpanded(isOpen ? null : key)}
                    className={isUn ? 'rmd-agent-row rmd-agent-row--unassigned' : 'rmd-agent-row'}
                  >
                    <td className="ds-strong">
                      <span className="rmd-agent">
                        {isOpen ? (
                          <ChevronDown size={14} className="rmd-chevron" aria-hidden />
                        ) : (
                          <ChevronRight size={14} className="rmd-chevron" aria-hidden />
                        )}
                        {a.agentName}
                      </span>
                    </td>
                    {types.map((t) => {
                      const c = byType.get(t.key);
                      const n = (c?.open ?? 0) + (c?.inProgress ?? 0);
                      return (
                        <td key={t.key} className="ds-num">
                          {n || '—'}
                        </td>
                      );
                    })}
                    <td className="ds-num rmd-bold">
                      {a.openTotal + a.inProgressTotal}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="rmd-subrow">
                      <td colSpan={types.length + 2}>
                        {a.types.length === 0 ? (
                          <span className="rm-caption">No tasks.</span>
                        ) : (
                          <Table caption={`${a.agentName} by task type`} className="rmd-subtable">
                            <thead>
                              <tr>
                                <th>Task type</th>
                                <th className="ds-num" title={METRIC_HELP[2].text}>Not started</th>
                                <th className="ds-num" title={METRIC_HELP[3].text}>In progress</th>
                                <th className="ds-num" title={METRIC_HELP[5].text}>Overdue</th>
                                <th className="ds-num" title={METRIC_HELP[6].text}>Past resolution</th>
                                <th className="ds-num" title={METRIC_HELP[8].text}>Closed</th>
                              </tr>
                            </thead>
                            <tbody>
                              {a.types.map((r) => (
                                <tr key={r.taskTypeKey}>
                                  <td className="ds-strong">{r.taskTypeName}</td>
                                  <td className="ds-num">{r.open || '—'}</td>
                                  <td className="ds-num">{r.inProgress || '—'}</td>
                                  <td className={r.overdue ? 'ds-num rmd-num--danger' : 'ds-num'}>{r.overdue || '—'}</td>
                                  <td className={r.pastResolution ? 'ds-num rmd-num--warning' : 'ds-num'}>{r.pastResolution || '—'}</td>
                                  <td className="ds-num">
                                    {isUn ? '—' : r.closed || '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td>Total</td>
              {types.map((t) => (
                <td key={t.key} className="ds-num">
                  {t.total}
                </td>
              ))}
              <td className="ds-num">{grand}</td>
            </tr>
          </tfoot>
        </Table>
      )}
    </Card>
  );
};

/**
 * One distinct colour per task type — enough for every type on the RM sheet
 * (11) plus a spare, so no two series share a colour.
 */
const TREND_COLORS = [
  '#6d28d9', // violet
  '#0f766e', // teal
  '#c2410c', // orange
  '#2563eb', // blue
  '#db2777', // pink
  '#65a30d', // green
  '#0891b2', // cyan
  '#ca8a04', // amber
  '#dc2626', // red
  '#475569', // slate
  '#a21caf', // fuchsia
  '#854d0e', // brown
];
const prettyType = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Leader view: task-mix created over time, with a day/week/month toggle. */
const TaskTrendsChart: React.FC = () => {
  const [gran, setGran] = useState<TrendGranularity>('week');
  const [data, setData] = useState<TaskTrends | null>(null);
  const [loading, setLoading] = useState(true);
  // Real type names ("Raise a concern", not the key "ticket") and a stable
  // colour per type — its place in the type list, not in this window's data,
  // so a type keeps its colour when you switch day/week/month.
  const [taskTypes, setTaskTypes] = useState<TaskTypeCfg[]>([]);

  useEffect(() => {
    tasksApi<TaskTypeCfg[]>('/v1/task-types')
      .then(setTaskTypes)
      .catch(() => setTaskTypes([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    tasksApi<TaskTrends>(`/v1/team-overview/trends?granularity=${gran}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [gran]);

  const typeOrder = [
    ...taskTypes.map((t) => t.key),
    ...(data?.types ?? []).filter((k) => !taskTypes.some((t) => t.key === k)), // retired types
  ];
  const colorFor = (type: string): string =>
    TREND_COLORS[Math.max(0, typeOrder.indexOf(type)) % TREND_COLORS.length];
  const nameOf = (type: string): string => taskTypes.find((t) => t.key === type)?.name ?? prettyType(type);
  const bucketTotal = (c: Record<string, number>): number =>
    Object.values(c).reduce((a, b) => a + b, 0);
  const max = data ? Math.max(1, ...data.series.map((s) => bucketTotal(s.counts))) : 1;
  const grandTotal = data ? data.series.reduce((a, s) => a + bucketTotal(s.counts), 0) : 0;

  return (
    <Card
      className="rmd-fill"
      title="Task trends"
      subtitle={`New tasks by type, per ${gran}.`}
      actions={
        <Segmented<TrendGranularity>
          size="sm"
          ariaLabel="Granularity"
          value={gran}
          onChange={setGran}
          options={(['day', 'week', 'month'] as TrendGranularity[]).map((g) => ({
            value: g,
            label: g === 'day' ? 'Daily' : g === 'week' ? 'Weekly' : 'Monthly',
          }))}
        />
      }
    >
      {loading ? (
        <Loader />
      ) : !data || grandTotal === 0 ? (
        <EmptyState title="No tasks created in this window." description="Try a longer granularity." />
      ) : (
        <>
          {/* Legend */}
          <div className="rmd-chart-legend">
            {data.types.map((tp) => (
              <span key={tp} className="rmd-chart-legend__item">
                <Swatch color={colorFor(tp)} />
                {nameOf(tp)}
              </span>
            ))}
          </div>

          {/* Stacked bars */}
          {(() => {
            const TRACK = 120;
            return (
              <div className="rmd-bars">
                {data.series.map((s) => {
                  const total = bucketTotal(s.counts);
                  return (
                    <div key={s.period} className="rmd-bars__col">
                      <div className="rmd-bars__value">
                        {total || ''}
                      </div>
                      {/* Track: bars grow up from a fixed baseline. */}
                      <div
                        className="rmd-bars__track rmd-bars__track--stacked"
                        title={data.types.map((tp) => `${nameOf(tp)}: ${s.counts[tp]}`).join('\n')}
                      >
                        {total === 0 ? (
                          <div className="rmd-bars__empty" />
                        ) : (
                          data.types.map((tp) =>
                            s.counts[tp] > 0 ? (
                              <div
                                key={tp}
                                className="rmd-bars__segment"
                                // Data-driven: segment height and per-type series colour.
                                style={{
                                  height: Math.max(3, (s.counts[tp] / max) * TRACK),
                                  background: colorFor(tp),
                                }}
                              />
                            ) : null,
                          )
                        )}
                      </div>
                      <div className="rmd-bars__label">
                        {s.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </>
      )}
    </Card>
  );
};

/** Health score → band tone (healthy 75+, watchlist 60–74, at risk 40–59, critical below 40). */
const healthBandTone = (v: number): BadgeTone =>
  HEALTH_TONE[v >= 75 ? 'HEALTHY' : v >= 60 ? 'WATCHLIST' : v >= 40 ? 'AT_RISK' : 'CRITICAL'];

/** Leader view: weekly team health — Overall (team avg) or By agent. */
const HealthTrendsChart: React.FC = () => {
  const [mode, setMode] = useState<'overall' | 'agent'>('overall');
  const [overall, setOverall] = useState<HealthTrends | null>(null);
  const [byAgent, setByAgent] = useState<AgentHealthTrends | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const url =
      mode === 'agent'
        ? '/v1/team-overview/health-trends?by=agent'
        : '/v1/team-overview/health-trends?granularity=week';
    tasksApi<HealthTrends | AgentHealthTrends>(url)
      .then((d) => {
        if (cancelled) return;
        if (mode === 'agent') setByAgent(d as AgentHealthTrends);
        else setOverall(d as HealthTrends);
      })
      .catch(() => {
        if (cancelled) return;
        if (mode === 'agent') setByAgent(null);
        else setOverall(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mode]);

  const H = 140;
  const overallSeries = overall?.series ?? [];
  const overallHasData = overallSeries.some((s) => s.average != null);
  const agents = byAgent?.agents ?? [];
  const agentSeries = byAgent?.series ?? [];
  const agentHasData = agentSeries.some((s) => agents.some((a) => s.byAgent[a.externalId] != null));

  return (
    <Card
      className="rmd-fill"
      title="Health trends"
      subtitle={mode === 'agent' ? 'Current health, by agent.' : 'Weekly average team health.'}
      actions={
        <Segmented<typeof mode>
          size="sm"
          ariaLabel="Health view"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'overall', label: 'Overall' },
            { value: 'agent', label: 'By agent' },
          ]}
        />
      }
    >
      {loading ? (
        <Loader />
      ) : mode === 'overall' ? (
        !overallHasData ? (
          <EmptyState title="No health scores yet." description="Scores appear once learners are assigned and scored." />
        ) : (
          <div className="rmd-bars rmd-bars--health">
            {overallSeries.map((s) => (
              <div key={s.period} className="rmd-bars__col">
                <div className="rmd-bars__value">
                  {s.average ?? ''}
                </div>
                <div className="rmd-bars__track rmd-bars__track--health">
                  {s.average != null ? (
                    <div
                      title={`${s.label}: ${s.average} avg · ${s.students} students`}
                      className={`rmd-bars__bar rmd-tone--${healthBandTone(s.average)}`}
                      // Data-driven: bar height from the score.
                      style={{ height: Math.max(3, (s.average / 100) * H) }}
                    />
                  ) : (
                    <div className="rmd-bars__empty rmd-bars__empty--bar" />
                  )}
                </div>
                <div className="rmd-bars__label">{s.label}</div>
              </div>
            ))}
          </div>
        )
      ) : !agentHasData ? (
        <EmptyState title="No health scores yet." description="Scores appear once learners are assigned and scored." />
      ) : (
        (() => {
          // Current health per agent = their most recent week's score.
          const current = agents
            .map((a) => {
              let score: number | null = null;
              for (let i = agentSeries.length - 1; i >= 0; i--) {
                const v = agentSeries[i].byAgent[a.externalId];
                if (v != null) { score = v; break; }
              }
              return { agent: a, score };
            })
            .filter((x) => x.score != null)
            .sort((a, b) => (b.score as number) - (a.score as number));
          return (
            <div className="rmd-bars rmd-bars--health rmd-bars--agents">
              {current.map(({ agent, score }) => (
                <div key={agent.externalId} className="rmd-bars__col">
                  <div className="rmd-bars__value">
                    {score}
                  </div>
                  <div className="rmd-bars__track rmd-bars__track--health">
                    <div
                      title={`${agent.name}: ${score}`}
                      className={`rmd-bars__bar rmd-tone--${healthBandTone(score as number)}`}
                      // Data-driven: bar height from the score.
                      style={{ height: Math.max(3, ((score as number) / 100) * H) }}
                    />
                  </div>
                  <div className="rmd-bars__label rmd-bars__label--agent" title={agent.name}>
                    {agent.name}
                  </div>
                </div>
              ))}
            </div>
          );
        })()
      )}
    </Card>
  );
};

/** The button on each "top action" row — what the RM does next, per type. */
const ACTION_LABEL: Record<string, string> = {
  renewal: 'Start renewal',
  onboarding: 'Onboard',
  first_class_not_scheduled: 'Schedule class',
  checkin_60_day: 'Check in',
  checkin_120_day: 'Check in',
  half_mark: 'Check in',
  callback: 'Call back',
  ticket: 'Resolve concern',
  survey_low_score: 'Follow up',
  survey_referral: 'Call referral',
  poor_class_rating: 'Follow up',
  refund: 'Handle refund',
  onboarding_callback: 'Call back',
  teacher_resignation: 'Find a teacher',
};

/**
 * Overdue means the action SLA was missed: nobody has touched it and its
 * deadline has passed. A ticket the RM is working isn't overdue however long
 * it stays open — that's "past resolution". Matches the API, the ticket list
 * and the SLA stat, so the dashboard's tiles and its breakdown agree.
 */
const isOverdue = (t: Task): boolean =>
  !!t.dueAt && !t.firstResponseAt && new Date(t.dueAt).getTime() < Date.now();

const isDueToday = (t: Task): boolean =>
  !!t.dueAt && !isOverdue(t) && new Date(t.dueAt).toDateString() === new Date().toDateString();

const fmtDue = (t: Task): string =>
  t.dueAt
    ? new Date(t.dueAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
    : 'no due date';

/** Donut showing the average health score, green arc = score / 100. */
const HealthDonut: React.FC<{ score: number }> = ({ score }) => {
  const r = 52;
  const c = 2 * Math.PI * r;
  return (
    <svg width={140} height={140} viewBox="0 0 140 140" role="img" aria-label={`Health ${score}`}>
      <circle className="rmd-donut__track" cx={70} cy={70} r={r} fill="none" strokeWidth={14} />
      <circle
        className="rmd-donut__arc"
        cx={70}
        cy={70}
        r={r}
        fill="none"
        strokeWidth={14}
        strokeLinecap="round"
        strokeDasharray={`${(score / 100) * c} ${c}`}
        transform="rotate(-90 70 70)"
      />
      <text className="rmd-donut__value" x={70} y={70} textAnchor="middle" dominantBaseline="central">
        {score}
      </text>
    </svg>
  );
};


const RmDashboardPage: React.FC = () => {
  const me = useMemo(currentActor, []);
  const navigate = useNavigate();
  const firstName = (me.name || 'there').split(' ')[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  // ── Live data: every non-completed task, ranked ────────────────────────
  const [openTasks, setOpenTasks] = useState<Task[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [portfolio, setPortfolio] = useState<PortfolioHealth | null>(null);
  const [learnerStat, setLearnerStat] = useState<{ total: number; addedThisMonth: number } | null>(null);
  const [tasksMe, setTasksMe] = useState<TasksMe | null>(null);
  const isLeader = isTasksLeaderRole(tasksMe?.role);

  // Own profile photo: `undefined` = show whatever the server has; a string
  // once the user uploads a new one this session.
  const [myPhoto, setMyPhoto] = useState<string | undefined>(undefined);
  const [photoBusy, setPhotoBusy] = useState(false);
  // The student whose detail drawer is open (null = closed).
  const [drawerStudent, setDrawerStudent] = useState<StudentRef | null>(null);
  const photoUrl = myPhoto !== undefined ? myPhoto : tasksMe?.photoUrl ?? null;

  const onPickPhoto = async (file: File): Promise<void> => {
    if (!file.type.startsWith('image/')) {
      toast.warn('Pick an image file');
      return;
    }
    setPhotoBusy(true);
    try {
      const url = await uploadMyPhoto(file);
      setMyPhoto(url);
      // Keep the header + cached identity in sync so a refresh/nav still shows it.
      setTasksMe((prev) => (prev ? { ...prev, photoUrl: url } : prev));
      patchCachedTasksMe({ photoUrl: url });
      toast.success('Photo updated');
    } catch (err: any) {
      toast.error(err.message || 'Photo update failed');
    } finally {
      setPhotoBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const open = await tasksApi<{ tasks: Task[]; total: number }>('/v1/tasks?open=true&take=200');
      // Leaders get an agent-wise overview; agents keep their personal cards.
      const mine = await fetchTasksMe();
      setTasksMe(mine);
      // Portfolio health is per-RM; a login that isn't provisioned as an
      // agent yet simply sees the empty state, not an error toast.
      if (me.externalId) {
        tasksApi<PortfolioHealth>(`/v1/agents/${me.externalId}/portfolio-health`)
          .then(setPortfolio)
          .catch(() => setPortfolio(null));
        tasksApi<{ mappings: { createdAt: string }[]; total: number }>(
          `/v1/agent-student-maps?agentExternalId=${me.externalId}&take=200`,
        )
          .then((res) => {
            const monthStart = new Date();
            monthStart.setDate(1);
            monthStart.setHours(0, 0, 0, 0);
            setLearnerStat({
              total: res.total,
              addedThisMonth: res.mappings.filter(
                (m) => new Date(m.createdAt) >= monthStart,
              ).length,
            });
          })
          .catch(() => setLearnerStat(null));
      }
      setTotal(open.total);
      setOpenTasks(
        [...open.tasks].sort((a, b) => {
          const byPriority = PRIORITY_WEIGHT[a.priority] - PRIORITY_WEIGHT[b.priority];
          if (byPriority !== 0) return byPriority;
          const byOverdue = Number(isOverdue(b)) - Number(isOverdue(a));
          if (byOverdue !== 0) return byOverdue;
          const aDue = a.dueAt ? new Date(a.dueAt).getTime() : Infinity;
          const bDue = b.dueAt ? new Date(b.dueAt).getTime() : Infinity;
          return aDue - bDue;
        }),
      );
    } catch (err: any) {
      toast.error(err.message || 'Task service unreachable');
    } finally {
      setLoading(false);
    }
  }, [me.externalId]);

  useEffect(() => {
    load();
  }, [load]);

  const maxSegment = portfolio
    ? Math.max(1, ...BAND_DISPLAY.map((b) => portfolio.bands[b.band]))
    : 1;
  const maxMonthly = portfolio?.monthly.length
    ? Math.max(...portfolio.monthly.map((m) => m.average))
    : 1;

  if (!RM_TASKS_ENABLED) {
    return (
      <Page>
        <EmptyState
          title="This feature isn't enabled in this environment yet."
          description="RM Tasks is switched off here; ask the team to enable it."
        />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        eyebrow="RM Tasks"
        title={`${greeting}, ${firstName}`}
        subtitle={isLeader ? 'Your team metrics' : 'Here is what needs your attention today.'}
        actions={
          /* Own avatar — click to update your photo */
          <label
            className={photoBusy ? 'rmd-photo rmd-photo--busy' : 'rmd-photo'}
            title={photoBusy ? 'Uploading…' : 'Update your photo'}
          >
            <Avatar key={photoUrl ?? ''} name={me.name || firstName} photoUrl={photoUrl} size="lg" className="rmd-photo__avatar" />
            <span className="rmd-photo__badge" aria-hidden>
              {photoBusy ? <span className="rmd-photo__spinner" /> : <Camera size={11} />}
            </span>
            <input
              type="file"
              accept="image/*"
              hidden
              disabled={photoBusy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onPickPhoto(f);
                e.target.value = '';
              }}
            />
          </label>
        }
      />

      {/* Attention banner — LIVE: overdue tasks + critical-health learners */}
      {(() => {
        const overdueTasks = openTasks.filter(isOverdue);
        const overdueCustomers = new Set(overdueTasks.map((t) => t.customerExternalId)).size;
        const overdueRenewals = overdueTasks.filter((t) => t.taskType.key === 'renewal').length;
        const overdueOthers = overdueTasks.length - overdueRenewals;
        const critical = portfolio?.bands.CRITICAL ?? 0;
        const needAttention = overdueCustomers + critical;
        if (needAttention === 0) return null;
        const parts = [
          overdueCustomers > 0 &&
            `${overdueCustomers} ${overdueCustomers === 1 ? 'has' : 'have'} overdue tasks`,
          critical > 0 && `${critical} ${critical === 1 ? 'is' : 'are'} at critical health`,
        ].filter(Boolean);
        const bannerBtn = (label: string, page: string) => (
          <Button
            key={label}
            size="sm"
            variant="secondary"
            onClick={() => navigate(`${process.env.PUBLIC_URL}/pages/${page}?urgency=overdue`)}
          >
            {label}
          </Button>
        );
        return (
          <Callout
            tone="warning"
            title={
              <span className="rmd-banner__title">
                <AlertTriangle size={16} aria-hidden />
                {needAttention} student{needAttention === 1 ? ' needs' : 's need'} immediate attention
              </span>
            }
            action={
              <div className="rm-row">
                {overdueRenewals > 0 && bannerBtn(`View renewals (${overdueRenewals})`, 'rm-renewals')}
                {overdueOthers > 0 && bannerBtn(`View tasks (${overdueOthers})`, 'rm-tasks')}
                {overdueTasks.length === 0 && critical > 0 && bannerBtn('View students', 'rm-learners')}
              </div>
            }
          >
            {parts.join(' and ')}.
          </Callout>
        );
      })()}

      {/* Per-agent metrics moved to Team Performance — this page is the
          RM's own queue again. */}
      {isLeader && <TaskTypeBreakdown />}

      {/* Stat cards — personal view only; leaders use Team overview above */}
      {!isLeader && (
      <div className="rm-stats">
        <button
          type="button"
          className="rmd-stat-link"
          title="Open My Students"
          onClick={() => navigate(`${process.env.PUBLIC_URL}/pages/rm-learners`)}
        >
          <StatTile
            label={
              <span className="rmd-stat-label">
                <span>My students</span>
                <ArrowUpRight size={14} aria-hidden />
              </span>
            }
            value={learnerStat ? learnerStat.total : '—'}
            hint={
              <span className="rmd-stat-hint rmd-text--success">
                {learnerStat
                  ? learnerStat.addedThisMonth > 0
                    ? `+${learnerStat.addedThisMonth} this month`
                    : 'No new students this month'
                  : 'No students mapped yet'}
              </span>
            }
          />
        </button>
        {(() => {
          const today = new Date().toDateString();
          // Renewals live on their own card — this one counts the rest,
          // mirroring the Tasks (non-renewal) queue.
          const nonRenewal = openTasks.filter((t) => t.taskType.key !== 'renewal');
          const overdueTasks = nonRenewal.filter(isOverdue).length;
          const dueToday = nonRenewal.filter(
            (t) => t.dueAt && !isOverdue(t) && new Date(t.dueAt).toDateString() === today,
          ).length;
          const renewals = openTasks.filter((t) => t.taskType.key === 'renewal');
          const renewalsOverdue = renewals.filter(isOverdue).length;
          const avg = portfolio?.averageScore ?? null;
          const prev = portfolio?.previousAverageScore ?? null;
          const mom = avg != null && prev != null ? Math.round((avg - prev) * 10) / 10 : null;
          const atRisk = portfolio?.bands.AT_RISK ?? 0;
          const critical = portfolio?.bands.CRITICAL ?? 0;

          const cards: {
            label: string;
            corner: string;
            value: string;
            valueSuffix?: string;
            sub: string;
            subTone: 'neutral' | 'success' | 'warning' | 'danger';
            valueTone: StatTone;
            /** Destination page — renders the corner arrow and makes the card clickable. */
            to?: string;
          }[] = [
            {
              label: 'Portfolio health',
              corner: 'MoM',
              value: avg != null ? String(avg) : '—',
              sub:
                mom == null
                  ? 'No prior month yet'
                  : `${mom >= 0 ? '↑' : '↓'} ${Math.abs(mom)} points`,
              subTone: mom == null ? 'neutral' : mom >= 0 ? 'success' : 'danger',
              valueTone: 'neutral',
            },
            {
              label: 'At risk / Critical',
              corner: '',
              value: String(atRisk),
              valueSuffix: `/ ${critical}`,
              sub:
                critical > 0
                  ? `${critical} need immediate action`
                  : atRisk > 0
                    ? `${atRisk} to watch`
                    : 'All students stable',
              subTone: critical > 0 ? 'danger' : atRisk > 0 ? 'warning' : 'success',
              valueTone: critical > 0 ? 'danger' : 'neutral',
            },
            {
              label: 'Tasks due today',
              corner: '',
              to: `${process.env.PUBLIC_URL}/pages/rm-tasks`,
              value: String(dueToday),
              sub: overdueTasks > 0 ? `${overdueTasks} overdue` : 'None overdue',
              subTone: overdueTasks > 0 ? 'danger' : 'success',
              valueTone: 'neutral',
            },
            {
              // Money opportunity needs payment amounts from the core
              // system — until then the sub shows overdue pressure instead.
              label: 'Renewal due',
              corner: 'open',
              to: `${process.env.PUBLIC_URL}/pages/rm-renewals`,
              value: String(renewals.length),
              sub: renewalsOverdue > 0 ? `${renewalsOverdue} overdue` : 'On track',
              subTone: renewalsOverdue > 0 ? 'danger' : 'success',
              valueTone: 'neutral',
            },
          ];
          return cards.map((c) => {
            const tile = (
              <StatTile
                tone={c.valueTone}
                label={
                  <span className="rmd-stat-label">
                    <span>{c.label}</span>
                    <span className="rmd-stat-corner">
                      {c.corner}
                      {c.to && <ArrowUpRight size={14} aria-hidden />}
                    </span>
                  </span>
                }
                value={
                  <>
                    {c.value}
                    {c.valueSuffix && (
                      <span className="rmd-stat-suffix">
                        {' '}
                        {c.valueSuffix}
                      </span>
                    )}
                  </>
                }
                hint={<span className={`rmd-stat-hint rmd-text--${c.subTone}`}>{c.sub}</span>}
              />
            );
            return c.to ? (
              <button
                key={c.label}
                type="button"
                className="rmd-stat-link"
                title={`Open ${c.label.toLowerCase()}`}
                onClick={() => navigate(c.to!)}
              >
                {tile}
              </button>
            ) : (
              <div key={c.label} className="rmd-stat-link rmd-stat-link--static">
                {tile}
              </div>
            );
          });
        })()}
      </div>
      )}

      <Row className="g-3">
        {/* Leaders get two trend charts; agents get their action list. */}
        {isLeader && (
          <>
            <Col lg={6}>
              <TaskTrendsChart />
            </Col>
            <Col lg={6}>
              <HealthTrendsChart />
            </Col>
          </>
        )}
        {!isLeader && (
          <Col lg={7}>
          <Card
            className="rmd-fill"
            title={
              <span className="rmd-card-title">
                <Swatch tone="danger" shape="dot" /> Top priority actions
              </span>
            }
            actions={
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate(`${process.env.PUBLIC_URL}/pages/rm-tasks`)}
              >
                View all {total} tasks <ArrowRight size={14} aria-hidden />
              </Button>
            }
          >
            {loading ? (
              <Loader label="Loading tasks…" />
            ) : openTasks.length === 0 ? (
              <EmptyState
                icon={<CheckCircle size={22} aria-hidden />}
                title="Nothing pending — all clear."
                description="New tasks show up here, most urgent first."
              />
            ) : (
              <div className="rmd-actions">
                {openTasks.map((t, i) => {
                  const late = isOverdue(t);
                  return (
                    <div key={t.id} className="rmd-action">
                      <span
                        className={`rmd-rank rmd-rank--${RANK_TONE[t.priority]}`}
                        title={t.priority}
                      >
                        {i + 1}
                      </span>
                      <div className="rmd-action__body">
                        <div className="rmd-action__title">
                          <CountryFlag phone={t.customerPhoneE164} />
                          {t.customerType === 'STUDENT' ? (
                            <button
                              type="button"
                              className="rmd-name-link"
                              title="View profile details"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDrawerStudent({
                                  studentExternalId: t.customerExternalId,
                                  name: t.customerName,
                                  phone: t.customerPhoneE164,
                                  email: t.customerEmail,
                                });
                              }}
                            >
                              {t.customerName}
                            </button>
                          ) : (
                            t.customerName
                          )}
                          {' — '}
                          {t.taskType.name}
                          <TaskIdChip taskNumber={t.taskNumber} uuid={t.id} className="ms-2" />
                        </div>
                        <div className="rmd-action__meta">
                          {t.status.label}
                          {' · '}
                          {late ? (
                            <span className="rmd-overdue">Overdue · was due {fmtDue(t)}</span>
                          ) : (
                            <>Due {fmtDue(t)}</>
                          )}
                          {' · '}
                          {t.assignee ? t.assignee.name : <em>Unassigned</em>}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={late || t.priority === 'URGENT' ? 'accent' : 'secondary'}
                        onClick={() => navigate(`${process.env.PUBLIC_URL}/pages/rm-tasks/${t.id}`)}
                      >
                        {ACTION_LABEL[t.taskType.key] ?? 'Open'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
          </Col>
        )}

        {/* Portfolio health — personal view only; leaders use Team overview */}
        {!isLeader && (
        <Col lg={5}>
          <Card
            className="rmd-fill"
            title="Portfolio health"
            actions={
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate(`${process.env.PUBLIC_URL}/pages/rm-learners`)}
              >
                Explore students <ArrowRight size={14} aria-hidden />
              </Button>
            }
          >
            {!portfolio || portfolio.learners.length === 0 ? (
              <EmptyState
                icon={<Activity size={22} aria-hidden />}
                title="No students mapped to you yet"
                description="Health appears once they are assigned and scored."
              />
            ) : (
            <>

            <div className="rmd-portfolio">
              <HealthDonut score={Math.round(portfolio.averageScore ?? 0)} />
              <div className="rmd-bands">
                {BAND_DISPLAY.map(({ band, label }) => (
                  <div key={band} className="rmd-band">
                    <span className="rmd-band__label">
                      {label}
                    </span>
                    <div className="rmd-band__track">
                      <div
                        className={`rmd-band__fill rmd-tone--${HEALTH_TONE[band]}`}
                        // Data-driven: bar length relative to the largest band.
                        style={{ width: `${Math.max(4, (portfolio.bands[band] / maxSegment) * 100)}%` }}
                      />
                    </div>
                    <span className="rmd-band__count">
                      {portfolio.bands[band]}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {portfolio.previousAverageScore != null && portfolio.averageScore != null && (
              <p className="rmd-note">
                Average health score {portfolio.averageScore >= portfolio.previousAverageScore ? 'improved' : 'slipped'} from{' '}
                <strong>{portfolio.previousAverageScore}</strong> → <strong>{portfolio.averageScore}</strong> this month.
              </p>
            )}

            <div className="rmd-monthly">
              {portfolio.monthly.map((m) => (
                <div key={m.month} className="rmd-monthly__col">
                  <div
                    className="rmd-monthly__bar"
                    // Data-driven: bar height from the month's average.
                    style={{ height: Math.max(6, (m.average / maxMonthly) * 120) }}
                    title={`${monthLabel(m.month)}: ${m.average}`}
                  />
                  <span className="rm-caption">{monthLabel(m.month)}</span>
                </div>
              ))}
            </div>
            </>
            )}
          </Card>
        </Col>
        )}
      </Row>
      <StudentDrawer student={drawerStudent} onClose={() => setDrawerStudent(null)} />
    </Page>
  );
};

export default RmDashboardPage;
